/** Watching an accepted job. The bench polls server-side; this just consumes the stream. */

import { request } from "./http";

export type JobOutcome = "running" | "succeeded" | "partial" | "failed" | "unknown";

export interface JobSnapshot {
  state: string | null;
  outcome: JobOutcome;
  finished: boolean;
  poll_count: number;
  elapsed_seconds: number;
  percent: number | null;
  stage: string | null;
  error: string | null;
  payload: unknown | null;
  /** Why watching stopped, when it stopped for a reason other than the job finishing. */
  detail: string | null;
}

export function fetchJobSnapshot(callId: string, signal?: AbortSignal): Promise<JobSnapshot> {
  return request<JobSnapshot>(`/api/calls/${encodeURIComponent(callId)}/job`, {
    ...(signal ? { signal } : {}),
  });
}

export interface JobStreamHandlers {
  onSnapshot: (snapshot: JobSnapshot) => void;
  onError: (detail: string) => void;
  onClose: () => void;
}

/**
 * Stream a job to completion.
 *
 * `EventSource` rather than fetch: it is the right primitive for server-sent events, and it
 * sends the session cookie automatically because the bench is same-origin. It also auto-
 * reconnects, which is wrong here — the stream is one job — so it is closed explicitly on the
 * terminal event.
 */
export function streamJob(callId: string, handlers: JobStreamHandlers): () => void {
  const source = new EventSource(`/api/calls/${encodeURIComponent(callId)}/job/stream`);
  let closed = false;

  const close = (): void => {
    if (!closed) {
      closed = true;
      source.close();
      handlers.onClose();
    }
  };

  const handleSnapshot = (event: MessageEvent<string>): void => {
    try {
      handlers.onSnapshot(JSON.parse(event.data) as JobSnapshot);
    } catch {
      handlers.onError("The bench sent a job update that could not be read.");
    }
  };

  source.addEventListener("progress", handleSnapshot);
  source.addEventListener("done", (event) => {
    handleSnapshot(event as MessageEvent<string>);
    close();
  });
  source.addEventListener("error", (event) => {
    // A server-sent `error` event carries a reason; a transport failure does not.
    const data = (event as MessageEvent<string>).data;
    if (typeof data === "string" && data) {
      try {
        handlers.onError(String((JSON.parse(data) as { detail?: unknown }).detail ?? data));
      } catch {
        handlers.onError(data);
      }
    } else {
      handlers.onError("The connection to the bench was lost while watching this job.");
    }
    close();
  });

  return close;
}
